import { Button, cn } from '@heroui/react'
import { motion } from 'framer-motion'
import { ChevronDown, ChevronUp, Star } from 'lucide-react'
import { useState } from 'react'

interface PricingCardProps {
  label: string
  monthlyPrice: string
  description: string
  features: string[]
  interval: string
  currency: string
  background?: string
  isCurrentPlan?: boolean
  isDisabled?: boolean
  isLoading?: boolean
  cta?: string
  onSelectPlan?: () => void
  showCurrentPlanBadge?: boolean
  hoverCard?: boolean
  minHeight?: string
  actionType?: 'cancel' | 'resume' | 'upgrade' | 'downgrade' | null
  actionLabel?: string
  onAction?: () => void
  isActionLoading?: boolean
  isSubscriptionManagementMode?: boolean
}

const PricingCard = ({
  label,
  monthlyPrice,
  description,
  features,
  currency,
  background,
  isCurrentPlan = false,
  isDisabled = false,
  isLoading = false,
  cta = 'Select Plan',
  onSelectPlan,
  showCurrentPlanBadge = false,
  hoverCard = true,
  minHeight,
  actionType,
  actionLabel,
  onAction,
  isActionLoading = false,
  isSubscriptionManagementMode = false,
}: PricingCardProps) => {
  const [showAllFeatures, setShowAllFeatures] = useState(false)
  const maxVisibleFeatures = 5

  return (
    <motion.div
      whileHover={hoverCard && !isDisabled ? 'hover' : undefined}
      className={cn(
        `relative w-80 border-2 shrink-0 rounded-[20px] p-7 flex flex-col self-start`,
        isCurrentPlan
          ? 'bg-[linear-gradient(180deg,#f0f9ff_0%,#e0f2fe_100%)] dark:bg-[linear-gradient(180deg,#0c4a6e_0%,#0e7490_100%)] border-blue-300 dark:border-blue-600'
          : background ||
              'bg-[linear-gradient(180deg,#ffffff_0%,#f9fafb_100%)] dark:bg-[linear-gradient(180deg,#15181D_0%,#02050A_100%)] border-gray-200 dark:border-[#181818]',
      )}
      style={{
        minHeight: minHeight || '400px',
      }}
      transition={{
        duration: 1,
        ease: 'backInOut',
      }}
      variants={{
        hover: {
          scale: 1.05,
        },
      }}
    >
      {/* Current Plan Badge */}
      {showCurrentPlanBadge && isCurrentPlan && (
        <div className="absolute top-4 right-4 z-20">
          <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
            <Star className="w-3 h-3" />
            <span>Current</span>
          </div>
        </div>
      )}

      <div className="relative z-10 dark:text-white flex flex-col flex-1">
        {/* Plan Label */}
        <div className="flex flex-col gap-3 mb-3">
          <span
            className={`text-xl font-semibold ${isCurrentPlan ? 'max-w-[calc(100%-90px)]' : ''}`}
          >
            {label}
          </span>
          <p className="font-light text-gray-700 dark:text-gray-300 leading-[1.5] min-h-[3.5rem]">
            {description}
          </p>
        </div>

        {/* Price Lockup */}
        <div className="mb-3">
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-semibold">
              {monthlyPrice === '0' ? 'Free' : monthlyPrice}
            </span>
            {monthlyPrice !== '0' && (
              <>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  {currency}
                </span>
                <span className="text-lg text-gray-600 dark:text-gray-400 ml-1">/mo</span>
              </>
            )}
          </div>
        </div>

        {/* Features */}
        {features && features.length > 0 && (
          <div className="flex-1 mb-3">
            <ul className="space-y-3">
              {(showAllFeatures ? features : features.slice(0, maxVisibleFeatures)).map(
                (feature: string, index: number) => (
                  <li key={index} className="flex items-start">
                    <span className="w-2 h-2 bg-black dark:bg-white rounded-full mt-2 mr-3 flex-shrink-0" />
                    <span
                      className="text-gray-700 dark:text-gray-300 leading-[1.5] break-all"
                      style={{ wordBreak: 'break-word' }}
                    >
                      {feature}
                    </span>
                  </li>
                ),
              )}
            </ul>
            {features.length > maxVisibleFeatures && (
              <button
                className="flex items-center gap-1 text-blue-600 dark:text-blue-400 text-sm font-medium mt-3 hover:underline"
                onClick={() => setShowAllFeatures(!showAllFeatures)}
              >
                {showAllFeatures ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show {features.length - maxVisibleFeatures} more
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons - Subscription Management Mode */}
      {isSubscriptionManagementMode && actionType && monthlyPrice !== '0' && (
        <Button
          className="w-full bg-blue-600 dark:bg-[#1E1E20] hover:bg-primary dark:hover:bg-blue-600 text-white transition-all duration-300 overflow-hidden"
          isDisabled={isDisabled}
          isLoading={isActionLoading}
          radius="sm"
          size="lg"
          title={actionLabel}
          onPress={onAction}
        >
          <span className="block truncate text-center w-full" title={actionLabel}>
            {actionLabel}
          </span>
        </Button>
      )}

      {/* Select Plan Button - Billing Page Mode */}
      {!isSubscriptionManagementMode && !isCurrentPlan && onSelectPlan && (
        <Button
          className="w-full bg-blue-600 dark:bg-[#1E1E20] hover:bg-primary dark:hover:bg-blue-600 text-white transition-all duration-300 overflow-hidden"
          isDisabled={isDisabled}
          isLoading={isLoading}
          radius="sm"
          size="lg"
          title={cta}
          onPress={onSelectPlan}
        >
          {cta}
        </Button>
      )}
    </motion.div>
  )
}

export default PricingCard
